<script lang="ts" setup>
import { useSdk } from '~/sdk';
import { handleBlogCmsError, useBlog } from '@/composables/useBlog';
import { useBlogPaths } from '@/composables/useBlogPaths';

const { locale, t } = useI18n();
const route = useRoute();
const { getIndexPath } = useBlogPaths();
const routeSlug = computed(() => {
  const slug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;

  return String(slug ?? '');
});

const { data: terms, error: termError, status: termStatus } = await useAsyncData(
  computed(() => `blog-term-${locale.value}-${routeSlug.value}`),
  async () => {
    return useSdk().cms.getContent({
      entries: {
        section: ['blogCategory', 'blogTag'],
        slug: routeSlug.value,
        level: false,
        fragment: `id title slug uri`,
      },
    });
  },
);

handleBlogCmsError(termError);

if (!terms.value?.length) {
  throw createError({ statusCode: 404, message: t('Page not found') });
}

const currentTerm = shallowRef(terms.value[0]);

watch([termStatus, terms], ([status, value]) => {
  if (status !== 'success') return;

  if (!value?.length) {
    showError(createError({ statusCode: 404, message: t('Page not found'), fatal: true }));
    return;
  }

  currentTerm.value = value[0];
});

const termId = computed(() => currentTerm.value?.id ?? null);
const termTitle = computed(() => currentTerm.value?.title ?? routeSlug.value);
const {
  currentPage,
  data: termArticles,
  hasPagination,
  loading,
  pageSize,
  totalItems,
} = await useBlog({ termId });

useSeoMeta({
  title: () => termTitle.value,
});
</script>

<template>
  <div v-if="currentTerm" class="py-16">
    <VContainer>
      <Breadcrumbs :breadcrumbs="[
        { title: $t('Home'), url: `/${locale}` },
        { title: 'Blog', url: getIndexPath() },
        { title: termTitle, url: '' },
      ]" class="mb-10" />

      <h1 class="text-3xl font-bold mb-8">{{ termTitle }}</h1>

      <div v-if="termArticles.length > 0" class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        <BlogCard v-for="article in termArticles" :key="article.id" :article="article" />
      </div>
      <p v-else-if="!loading" class="text-gray-500">{{ $t('blog.No articles found') }}</p>

      <PaginationQuery
        v-if="hasPagination"
        :current-page="currentPage"
        :total-items="totalItems"
        :page-size="pageSize"
      />
    </VContainer>
  </div>
</template>
